import { useEffect, useMemo, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";
import type { SubtitleFilter } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useDurationSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDurationSubtitleFilter";
import { allTrackers } from "app/services/track/track";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TableFooter } from "../../../common/overview/StyledComponents";
import { AdIntelligenceHookButton } from "../../../common/shared/TableComponents";
import { WidgetBoxWithMarginBottom } from "../../../common/StyledComponents";
import {
    calculateApiPage,
    FIRST_PAGE,
    getNoDataObject,
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    openAdIntelligenceHookModal,
    PAGE_SIZE_LARGE,
    PAGE_SIZE_SMALL,
    slicePaginatedData,
} from "../../../common/utils/utils";
import { initialTableSort } from "../../campaigns/DisplayAdvertisingCampaigns";
import { useTableRows } from "../../campaigns/useTableRows";

import { getColumns } from "./RecentCampaignsTableColumns";

export const TOP_CAMPAIGNS_TABLE_ID = "top-campaigns-table";

interface IRecentCampaignsTableProps {
    withPagination?: boolean;
    title?: string;
    isPreview?: boolean;
}

export const RecentCampaignsTable = ({
    withPagination = false,
    title = "ad.intelligence.advertiser.top.campaigns.table.title",
    isPreview = false,
}: IRecentCampaignsTableProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const isCompare = chosenSites.isCompare();
    const selectedCampaignsSite = params.campaignsSelectedSite ?? params.key.split(",")[0];

    const apiPage = withPagination ? calculateApiPage(currentPage) : currentPage;
    const pageSize = withPagination ? PAGE_SIZE_LARGE : PAGE_SIZE_SMALL;

    const { data, isLoading, isError } = useTableRows(
        selectedCampaignsSite,
        apiPage,
        pageSize,
        undefined,
        initialTableSort,
    );

    // Reset to page 1 when params change (domain, duration, country) and withPagination is true
    useEffect(() => {
        if (withPagination) {
            setCurrentPage(FIRST_PAGE);
        }
    }, [params.key, params.duration, params.country, params.campaignsSelectedSite, withPagination]);

    const paginatedData = useMemo(() => {
        if (!withPagination) {
            return data?.items;
        }
        return slicePaginatedData(data?.items, currentPage);
    }, [withPagination, data?.items, currentPage]);

    const durationSubtitleFilter = useDurationSubtitleFilter(data?.startDate);
    const combinedSubtitleFilters: SubtitleFilter[] = [durationSubtitleFilter, ...subtitleFilters];
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(isPreview);

    const onSelectSite = (selectedSite: string) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite}`);
        proNavigator.applyUpdateParams({
            campaignsSelectedSite: selectedSite,
        });
    };

    const handleSeeAll = () => {
        if (hasAdIntelBeta) {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Top_campaigns/see_more");
            void displayAdvertisingNavigation.campaigns.go(params);
        } else {
            openAdIntelligenceHookModal();
        }
    };

    const noDataObject = getNoDataObject(isError);

    return (
        <WidgetBoxWithMarginBottom id={TOP_CAMPAIGNS_TABLE_ID}>
            <Table
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={getColumns(params, { isPreview })}
                data={paginatedData}
                isLoading={isLoading}
                loaderRowCount={5}
                noData={noDataObject}
                withArrowButtons={!!data?.totalCount}
                pagination={
                    withPagination
                        ? {
                              pageSize: PAGE_SIZE_SMALL,
                              total: data?.totalCount ?? 0,
                              current: currentPage,
                              onChange: (_page, _pageSize, nextPage) => {
                                  setCurrentPage(nextPage);
                              },
                          }
                        : undefined
                }
            >
                <Table.TableTop
                    borderBottom
                    withoutRowBorders
                >
                    <Table.TableTop.Title
                        withFiltersLoader
                        title={translate(title)}
                        tooltip={translate("ad.intelligence.overview.top.campaigns.table.title.tooltip")}
                        filters={combinedSubtitleFilters}
                    />
                    {isCompare ? (
                        <CompareTabsContainer
                            onSelectSite={onSelectSite}
                            keyName="campaignsOverviewFirstLoads"
                            domainQueryParamName={"campaignsSelectedSite"}
                            hideResults={true}
                        />
                    ) : null}
                </Table.TableTop>
            </Table>
            {data?.totalCount && !withPagination ? (
                <TableFooter>
                    {hasAdIntelBeta ? (
                        <IconButton
                            type="flat"
                            onClick={handleSeeAll}
                            iconName="arrow-right"
                            placement="right"
                            iconSize="xs"
                        >
                            {translate("ad.intelligence.overview.top.campaigns.table.see.all.button", {
                                totalCount: numberFilter()(data.totalCount),
                            })}
                        </IconButton>
                    ) : (
                        <AdIntelligenceHookButton
                            dataAutomation="ad-intelligence-recent-campaigns-hook-button"
                            buttonText={translate(
                                "ad.intelligence.advertiser.overview.recent.campaigns.preview.button",
                            )}
                            isNoTouchVariant={isNoTouchVariant}
                            onButtonClick={handleSeeAll}
                        />
                    )}
                </TableFooter>
            ) : null}
        </WidgetBoxWithMarginBottom>
    );
};
